import { Typography } from "@material-ui/core";
import React from "react";
import {
  ArrayField,
  ArrayInput,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  FormTab,
  List,
  NumberInput,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleFormIterator,
  Tab,
  TabbedForm,
  TabbedFormTabs,
  TabbedShowLayout,
  TextField,
  TextInput,
  Toolbar,
} from "react-admin";
import EditReadOnlyButton from "../components/Buttons/EditReadOnlyButton";
import PlaceInput from "../components/Input/PlaceInput";
import SabreConfigInput from "../components/Input/SabreConfigInput";
import SidebarFormLayout from "../components/SidebarFormLayout";
import { AmadeusConfigsInputs } from "./agencies";
import {
  AnalyticsConfigInputs,
  ApprovalSecurityInputs,
  CarRentalConfigInputs,
  CustomerConfigFields,
  CustomerConfigInputs,
  DangerZoneInputs,
  MissionOrderConfigInputs,
  sabreProfileType,
  TravelSearchConfigInputs,
} from "./companies";

const styles = {
  title: {
    marginTop: "30px",
    marginBottom: "15px",
    fontSize: "20px",
    fontWeight: "bold",
  },
  subtitle: {
    marginTop: "24px",
    fontWeight: "bold",
  },
};

const EntityFilter = (props) => (
  <Filter {...props}>
    <NumberInput label="ID" source="uid" alwaysOn />
    <ReferenceInput
      label="Entreprise"
      source="company_id"
      reference="companies"
      filterToQuery={(q) => ({ name: q })}
      perPage={50}
      alwaysOn
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
    <TextInput source="name" label="Nom" alwaysOn />
    <TextInput source="sabre_profile_name" label="Profil Sabre" />
    <TextInput source="sabre_profile_domain_id" label="Domaine Sabre" />
  </Filter>
);

const ReadOnlyToggle = ({ record, ...props }) => (
  <EditReadOnlyButton record={record} {...props} entity="entities" />
);

export const EntityList = ({ permissions, ...props }) => (
  <List
    {...props}
    title="Liste des entités"
    bulkActionButtons={false}
    filters={<EntityFilter />}
    sort={{ field: "id", order: "ASC" }}
  >
    <Datagrid rowclick="show" perPage={200}>
      <TextField source="id" label="Id" />
      <TextField source="name" label="Nom" />
      <TextField source="company.name" label="Entreprise" sortBy="company_id" />
      <BooleanField source="is_open" label="Ouvert" sortable={false} />
      <BooleanField
        source="config.readOnly"
        label="Lecture seule"
        sortable={false}
      />
      <ShowButton label="" />
      <EditButton label="" />
      {permissions != null && permissions.includes("dev") && <ReadOnlyToggle />}
    </Datagrid>
  </List>
);

export const EntityCreate = ({ permissions, ...props }) => (
  <SidebarFormLayout>
    <Create {...props}>
      <TabbedForm
        redirect="list"
        tabs={<TabbedFormTabs orientation="vertical" />}
      >
        <FormTab label="Informations générales">
          <TextInput source="name" label="Nom" validate={[required()]} />
          <ReferenceInput
            label="Entreprise"
            source="company_id"
            reference="companies"
            filterToQuery={(q) => ({ name: q })}
            perPage={50}
            validate={[required()]}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <BooleanInput source="is_open" label="Ouvert" />
          <PlaceInput source="place" label="Adresse" />
          <TextInput source="contact" label="Contact" />
          <BooleanInput
            source="corsican_fares_enabled"
            label="Tarifs corses activés"
          />
          <ReferenceInput
            source="config.travelAgencyID"
            label="Agence de voyage"
            reference="agencies"
            filterToQuery={(q) => ({ name: q })}
            perPage={50}
            allowEmpty
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          {permissions != null && permissions.includes("admin") && (
            <ArrayInput
              source="config.analyticValues"
              label="Valeurs analytiques"
            >
              <SimpleFormIterator>
                <TextInput source="analytic.key" label="Clé SABRE/Amadeus" />
                <TextInput source="analytic.cds_key" label="Clé CDS" />
                <TextInput source="value" label="Valeur" />
              </SimpleFormIterator>
            </ArrayInput>
          )}
        </FormTab>

        {/* Tab 2: Configuration SNCF */}
        <FormTab label="Configuration SNCF">
          <TextInput source="config.fce" label="FCE SNCF" />
        </FormTab>

        {/* Tab 3: Services externes (admin) */}
        {permissions != null &&
          permissions.includes("admin") &&
          CustomerConfigInputs("Services externes")}

        {/* Tab 3: Configuration Amadeus */}
        <FormTab label="Configuration Amadeus">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("amadeus")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("amadeus"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Amadeus
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Amadeus à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.amadeusCustomerProfile.officeID"
                      label="Office ID Amadeus"
                    />
                    <TextInput
                      source="config.amadeusCustomerProfile.uniqueID"
                      label="Unique ID Amadeus"
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Amadeus
                    </Typography>
                    {AmadeusConfigsInputs({
                      sourcePrefix: "config.amadeusConfigs.",
                    })}
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Amadeus dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 4: Configurer Sabre */}
        <FormTab label="Configurer Sabre">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("sabre")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("sabre"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Sabre
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à charger sur les PNR
                    </Typography>
                    <TextInput
                      source="config.sabreProfile.pcc"
                      label="PCC Sabre"
                    />
                    <TextInput
                      source="config.sabreProfile.name"
                      label="Nom profil Sabre"
                    />
                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.sabrePaxCustomerProfile.number"
                      label="Numéro profil voyageur"
                    />
                    <TextInput
                      source="config.sabrePaxCustomerProfile.pcc"
                      label="PCC profil voyageur"
                    />
                    <SelectInput
                      source="config.sabrePaxCustomerProfile.type"
                      label="Type de profil"
                      alwaysOn
                      allowEmpty
                      choices={sabreProfileType}
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Sabre
                    </Typography>
                    <SabreConfigInput sourcePrefix="config.sabreConfigs" />
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Sabre dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>
      </TabbedForm>
    </Create>
  </SidebarFormLayout>
);

const EntityCustomToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

// ---------------------------------------------------------------------------
// EntityEdit — 11 tabs
// ---------------------------------------------------------------------------

export const EntityEdit = ({ permissions, ...props }) => (
  <SidebarFormLayout>
    <Edit {...props}>
      <TabbedForm
        redirect="list"
        toolbar={<EntityCustomToolbar />}
        tabs={<TabbedFormTabs orientation="vertical" />}
      >
        {/* Tab 1: Informations générales (enhanced) */}
        <FormTab label="Informations générales">
          <TextInput disabled label="Id" source="uid" />
          <TextInput source="name" label="Nom" validate={[required()]} />
          <ReferenceInput
            label="Entreprise"
            source="company_id"
            reference="companies"
            filterToQuery={(q) => ({ name: q })}
            perPage={50}
            validate={[required()]}
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
          <BooleanInput source="is_open" label="Ouvert" />
          <PlaceInput source="place" label="Adresse" />
          <TextInput source="contact" label="Contact" />
          <BooleanInput
            source="corsican_fares_enabled"
            label="Tarifs corses activés"
          />
          <ReferenceInput
            source="config.travelAgencyID"
            label="Agence de voyage"
            reference="agencies"
            filterToQuery={(q) => ({ name: q })}
            perPage={50}
            allowEmpty
          >
            <AutocompleteInput optionText="name" />
          </ReferenceInput>
        </FormTab>

        {/* Tab 2: Configuration SNCF */}
        <FormTab label="Configuration SNCF">
          <TextInput source="config.fce" label="FCE SNCF" />
        </FormTab>

        {/* Tab 3: Voyages & Recherche (admin) — shared config inputs */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Voyages & Recherche">
            {TravelSearchConfigInputs()}
          </FormTab>
        )}

        {/* Tab 3: Approbation & Sécurité (admin) — shared */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Approbation & Sécurité">
            {ApprovalSecurityInputs()}
          </FormTab>
        )}

        {/* Tab 4: Analytiques (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Analytiques">
            {AnalyticsConfigInputs()}

            <Typography variant="h5" style={styles.title}>
              Valeurs analytiques utilisateur surchargées
            </Typography>
            <ArrayInput
              source="overwritten_user_analytic_values"
              label="Surcharges analytiques utilisateur"
            >
              <SimpleFormIterator>
                <TextInput source="analytic.key" label="Clé analytique" />
                <TextInput source="value" label="Valeur" />
                <TextInput
                  source="user_facing_error"
                  label="Message d'erreur"
                />
              </SimpleFormIterator>
            </ArrayInput>
          </FormTab>
        )}

        {/* Tab 5: Services externes (admin) — shared CustomerConfigInputs */}
        {permissions != null &&
          permissions.includes("admin") &&
          CustomerConfigInputs("Services externes")}

        {/* Tab 6: Configuration Amadeus */}
        <FormTab label="Configuration Amadeus">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("amadeus")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("amadeus"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Amadeus
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Amadeus à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.amadeusCustomerProfile.officeID"
                      label="Office ID Amadeus"
                    />
                    <TextInput
                      source="config.amadeusCustomerProfile.uniqueID"
                      label="Unique ID Amadeus"
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Amadeus
                    </Typography>
                    {AmadeusConfigsInputs({
                      sourcePrefix: "config.amadeusConfigs.",
                    })}
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Amadeus dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 7: Configurer Sabre */}
        <FormTab label="Configurer Sabre">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              ((formData.config.providers.air &&
                formData.config.providers.air.includes("sabre")) ||
                (formData.config.providers.rail &&
                  formData.config.providers.rail.includes("sabre"))) ? (
                  <>
                    <Typography variant="h5" style={styles.title}>
                    Configurer Sabre
                    </Typography>

                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à charger sur les PNR
                    </Typography>
                    <TextInput
                      source="config.sabreProfile.pcc"
                      label="PCC Sabre"
                    />
                    <TextInput
                      source="config.sabreProfile.name"
                      label="Nom profil Sabre"
                    />
                    <Typography variant="h5" style={styles.title}>
                    Profil Sabre à lier aux profils créés
                    </Typography>
                    <TextInput
                      source="config.sabrePaxCustomerProfile.number"
                      label="Numéro profil voyageur"
                    />
                    <TextInput
                      source="config.sabrePaxCustomerProfile.pcc"
                      label="PCC profil voyageur"
                    />
                    <SelectInput
                      source="config.sabrePaxCustomerProfile.type"
                      label="Type de profil"
                      alwaysOn
                      allowEmpty
                      choices={sabreProfileType}
                    />

                    <Typography variant="h5" style={styles.title}>
                    Surcharger la configuration Sabre
                    </Typography>
                    <SabreConfigInput sourcePrefix="config.sabreConfigs" />
                  </>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez le fournisseur Sabre dans « Services externes » pour
                  configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 8: Location de voitures */}
        <FormTab label="Location de voitures">
          <FormDataConsumer>
            {({ formData }) =>
              formData.config &&
              formData.config.providers &&
              formData.config.providers.carRental &&
              formData.config.providers.carRental.length > 0 ? (
                  <>{CarRentalConfigInputs()}</>
                ) : (
                  <Typography style={{ color: "#999", fontStyle: "italic" }}>
                  Activez un fournisseur de location de voitures dans « Services
                  externes » pour configurer cet onglet.
                  </Typography>
                )
            }
          </FormDataConsumer>
        </FormTab>

        {/* Tab 9: Ordres de mission (dev) */}
        {permissions != null && permissions.includes("dev") && (
          <FormTab label="Ordres de mission">
            <BooleanInput
              source="config.missionOrderConfig.enabled"
              label="Ordres de mission activés"
            />
            <FormDataConsumer>
              {({ formData }) =>
                formData.config &&
                formData.config.missionOrderConfig &&
                formData.config.missionOrderConfig.enabled && (
                  <MissionOrderConfigInputs />
                )
              }
            </FormDataConsumer>
          </FormTab>
        )}

        {/* Tab 10: Facturation (admin) */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Facturation">
            <Typography variant="h5" style={styles.title}>
              Facturation MCTO
            </Typography>
            <TextInput
              source="config.billingConf.customerName"
              label="Identifiant client MCTO"
              helperText="Ex: FC0059379"
            />
            <BooleanInput
              source="config.billingConf.enabled"
              label="Activer les MCTO"
            />
          </FormTab>
        )}

        {/* Tab 12: Zone de danger (admin) — shared */}
        {permissions != null && permissions.includes("admin") && (
          <FormTab label="Zone de danger">{DangerZoneInputs()}</FormTab>
        )}
      </TabbedForm>
    </Edit>
  </SidebarFormLayout>
);

export const EntityShow = ({ permissions, ...props }) => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Informations générales">
        <TextField source="id" label="Id" />
        <TextField source="name" label="Nom" />
        <TextField source="company.name" label="Entreprise" sortable={false} />
        <TextField source="config.fce" label="FCE SNCF" />
        <BooleanField source="is_open" label="Ouvert" />
        {permissions != null && permissions.includes("admin") && (
          <ArrayField
            source="config.analyticValues"
            label="Valeurs analytiques"
          >
            <Datagrid>
              <TextField source="analytic.key" label="Clé SABRE/Amadeus" />
              <TextField source="analytic.cds_key" label="Clé CDS" />
              <TextField source="value" label="Valeur" />
            </Datagrid>
          </ArrayField>
        )}
      </Tab>
      {permissions != null &&
        permissions.includes("admin") &&
        CustomerConfigFields("Configuration avancée")}
    </TabbedShowLayout>
  </Show>
);
